import {filterAllowedHeaders} from '@udemy-v2/nextjs-core';
import {HTTP_REQUEST_CONTEXT_HEADER} from '@udemy-v2/nextjs-core';
import {env} from '../../../../next.config.env';

/**
 * Resolves request context headers required for API authorization
 *
 * If context headers aren't present, attempts to:
 * 1. Extract and exchange access token for JWT if available
 * 2. Fall back to development context headers from global state
 */
export async function getRequestContextHeaders(_headers: Readonly<Headers>, url?: string) {
    // Ensure consistent access behavior.
    const headers = new Headers(_headers);
    // We need to create a `headersObject` so we can extend it
    let headersObject = Object.fromEntries(headers?.entries?.() ?? []);

    // Detect if we have request context headers
    // If we do, it's because we are processing a request behind API gateway
    // If not, we are probably in a local or Skaffold environment.  Attempt to resolve from access token or generate one.
    const hasRequestContextHeaders = headers.get(HTTP_REQUEST_CONTEXT_HEADER);
    if (!hasRequestContextHeaders && env.DEPLOY_ENV !== 'prod') {
        const hasAccessToken = headers.get('cookie')?.includes('access_token');
        if (hasAccessToken) {
            // Grab access token and exchange it for a JWT
            const monolithAccessToken = headers
                ?.get('cookie')
                ?.split('; ')
                .find((row) => row.startsWith('access_token='))
                ?.split('=')[1];

            console.debug('getRequestContextHeaders(): user presents access token', {
                monolithAccessToken,
            });

            try {
                const authorizedContextHeaders =
                    await global?.getAuthorizedContextHeadersFromMonolithAccessToken(
                        monolithAccessToken as string,
                    );
                console.debug('getRequestContextHeaders(): authorizedContextHeaders resolved', {
                    authorizedContextHeaders,
                });
                headersObject = {
                    ...headersObject,
                    ...authorizedContextHeaders,
                };
            } catch (error) {
                console.error(
                    `getRequestContextHeaders(): error exchanging access token for JWT for request ${url}`,
                    error,
                );
            }
        } else {
            const devContextHeaders = global.requestContextResolver?.contextHeaders;
            console.debug(
                'getRequestContextHeaders(): no access_token presented.  Use devContextHeaders',
                {devContextHeaders},
            );
            // These are generated from `src/instrumentation.ts`
            headersObject = {
                ...headersObject,
                ...devContextHeaders,
            };
        }
    } else {
        console.debug('getRequestContextHeaders(): request context headers already present');
    }

    return filterAllowedHeaders(headersObject);
}
